import { useEffect, useRef, useState } from 'react'
import throttle from 'lodash-es/throttle'

import { EVENT_NAME_SCROLL } from '../../constants/event'

import { getNaturalOffsetTop, getScrollContext } from './useStickyShadow.utils'

// Detects whether a `position: sticky` element is currently held against
// scroll so consumers can elevate it (e.g. apply a shadow). At mount we
// capture the element's natural top within its scroll ancestor; on scroll
// the element is "stuck" iff `scrollTop` has moved past that natural top.
// An IntersectionObserver alone cannot tell apart "naturally at top: 0"
// from "pinned at top: 0", which is why we compare scroll positions.
export function useStickyShadow() {
  const [isStuck, setIsStuck] = useState(false)

  const ref = useRef(null)

  useEffect(() => {
    const element = ref.current

    if (element) {
      const { eventTarget, scrollElement } = getScrollContext(element)
      const naturalTop = getNaturalOffsetTop(element, scrollElement)

      function update() {
        setIsStuck(scrollElement.scrollTop > naturalTop)
      }

      const throttledUpdate = throttle(update, 100)

      update()

      eventTarget.addEventListener(EVENT_NAME_SCROLL, throttledUpdate, {
        passive: true,
      })

      return () => {
        eventTarget.removeEventListener(EVENT_NAME_SCROLL, throttledUpdate)

        throttledUpdate.cancel()
      }
    }
  }, [])

  return { isStuck, ref }
}
